import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// createSpace now handled through DataContext
import { useUnifiedData } from '../../../contexts/AppDataContext';
import { useSpaceContext } from '../../../contexts/AppDataContext';
import '../../../Styles/Space/Spaces Page/CreateSpaceModal.css';
import { CloseButton } from '../../Layout/Buttons/CloseButton';

export const CreateSpaceModal: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { setIsCreatingSpace, isCreatingSpace } = useSpaceContext();
  const [formData, setFormData] = useState({
    title: '',
    instructions: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const draftKey = useRef(`study-space-draft-${Date.now()}`).current;
  const modalRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const instructionsInputRef = useRef<HTMLTextAreaElement>(null);
  
  const { addNewSpace } = useUnifiedData();
  
  // Load draft from localStorage on mount
  useEffect(() => {
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsedData = JSON.parse(draft);
        setFormData(parsedData);
      } catch (e) {
        console.error('Error parsing draft data:', e);
      }
    }
  }, [draftKey]);
  
  // Save draft to localStorage while typing
  useEffect(() => {
    const saveTimeout = setTimeout(() => {
      localStorage.setItem(draftKey, JSON.stringify(formData));
    }, 500);
    
    return () => clearTimeout(saveTimeout);
  }, [formData, draftKey]);

  // Autofocus input fields when step changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (step === 1 && titleInputRef.current) {
        titleInputRef.current.focus();
      } else if (step === 2 && instructionsInputRef.current) {
        instructionsInputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [step]);

  // Validation helpers
  const isValidTitle = useCallback(() => {
    return formData.title.trim().length >= 3;
  }, [formData.title]);

  const isValidInstructions = useCallback(() => {
    return formData.instructions.trim().length >= 10;
  }, [formData.instructions]);
  
  // Check if step is complete
  const isStepComplete = useCallback((stepNumber: number) => {
    if (stepNumber === 1) return isValidTitle();
    if (stepNumber === 2) return isValidInstructions();
    return false;
  }, [isValidTitle, isValidInstructions]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear errors when user types
    if (error) setError(null);
  };

  const handleNext = useCallback(() => {
    if (step < 2 && isStepComplete(step)) {
      setStep(step + 1);
    }
  }, [step, isStepComplete]);

  const handleBack = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
    }
  }, [step]);

  const handleClose = useCallback(() => {
    // Only prompt if there's data and not submitting
    if (!loading && (formData.title.trim() || formData.instructions.trim())) {
      const confirmed = window.confirm('Are you sure you want to close? Your draft will be saved, but not published.');
      if (!confirmed) return;
    }
    
    setIsCreatingSpace(false);
  }, [loading, formData, setIsCreatingSpace]);

  const handleSubmit = useCallback(async () => {
    if (!isValidTitle() || !isValidInstructions()) {
      setError('Please complete all required fields');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Use Convex through DataContext
      const newSpace = await addNewSpace(formData.title.trim(), formData.instructions.trim());
      
      // Clear draft after successful creation
      localStorage.removeItem(draftKey);
      
      // Close modal and navigate to new space
      setIsCreatingSpace(false);
      navigate(`/spaces/${newSpace.id}`);
    } catch (error) {
      console.error('Error creating space:', error);
      setError('Failed to create space. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formData.title, formData.instructions, isValidTitle, isValidInstructions, setIsCreatingSpace, navigate, draftKey, addNewSpace]);


  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle escape key to close modal
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
        return;
      }
      
      // Handle enter key to progress through steps
      if (e.key === 'Enter' && !e.shiftKey) {
        if (step === 1 && isValidTitle()) {
          e.preventDefault();
          handleNext();
        } else if (step === 2 && isValidInstructions()) {
          e.preventDefault();
          handleSubmit();
        }
      }
    };
    
    const currentModalRef = modalRef.current;
    if (currentModalRef) {
      currentModalRef.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      if (currentModalRef) {
        currentModalRef.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [step, formData, handleNext, handleClose, handleSubmit, isValidTitle, isValidInstructions]);


  
  if (!isCreatingSpace) return null;

  return (
    <div className="create-space-modal-overlay" onClick={handleClose}>
      <div 
        className="create-space-modal-content"
        ref={modalRef}
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <div className="create-space-modal-header">
          <h2>Create New Space</h2>
          <CloseButton onClick={handleClose} />
        </div>

        <div className="create-space-progress">
          {[1, 2].map(i => (
            <div 
              key={i}
              className={`progress-step ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
              onClick={() => i < step && setStep(i)}
              style={{ cursor: i < step ? 'pointer' : 'default' }}
            >
              <div className="step-number">{i}</div>
            </div>
          ))}
        </div>

        <div className="create-space-modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {step === 1 && (
            <div className="create-space-form-step">
              <h3 className="create-space-form-title">What would you like to explore?</h3>
              <p>Give your Space a clear, descriptive title that reflects what you want to accomplish.</p>
              <input
                ref={titleInputRef}
                type="text"
                name="title"
                placeholder="Enter a title for your space..."
                value={formData.title}
                onChange={handleInputChange}
                className="modal-input"
                autoFocus
                minLength={3}
                disabled={loading}
                aria-label="Space title"
              />
              <div className="validation-message">
                {formData.title.length > 0 && !isValidTitle() && 'Title should be at least 3 characters'}
              </div>
              <button 
                className="primary-button"
                onClick={handleNext}
                disabled={!isValidTitle() || loading}
              >
                Continue
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="create-space-form-step">
              <h3 className="create-space-form-title">What would you like to achieve?</h3>
              <p>Describe your learning objectives and what you'd like to accomplish in this space.</p>
              <textarea
                ref={instructionsInputRef}
                name="instructions"
                placeholder="Describe your goals, questions, or topics you want to explore..."
                value={formData.instructions}
                onChange={handleInputChange}
                className="modal-input"
                rows={5}
                autoFocus
                minLength={10}
                disabled={loading}
                aria-label="Learning objectives"
              />
              <div className="validation-message">
                {formData.instructions.length > 0 && !isValidInstructions() && 'Goals & Objectives should be at least 10 characters'}
              </div>
              <div className="button-group">
                <button 
                  className="secondary-button"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Back
                </button>
                <button 
                  className="primary-button"
                  onClick={handleSubmit}
                  disabled={!isValidInstructions() || loading}
                >
                  {loading ? (
                      <div className="loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                  ) : (
                    'Create Space'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateSpaceModal;
